import React, { useContext, useState, useEffect, forwardRef } from "react";
import MaterialTable from "material-table";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FirstPage,
  LastPage,
  FilterList,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";

// Contexts
import CampaignContext from "../../../context/campaign/CampaignContext";
import PatchedPagination from "../../layout/MTPagination";

const useStyles = makeStyles(() => ({
  testBorder: {
    border: "1px solid",
  },
  searchRow: {
    height: "64px",
    backgroundColor: "#fff",
    borderTopLeftRadius: "5px",
    borderTopRightRadius: "5px",
  },
  searchTitle: {
    paddingLeft: "24px",
  },
  searchInput: {
    fontSize: "0.9rem",
    paddingRight: "8px",
    width: "228px",
  },
  button: {
    marginRight: 7,
    padding: 5,
    textTransform: "none",
    "&:focus": {
      outline: "none",
    },
  },
  tableHeader: {
    fontWeight: "bold",
    fontSize: 13,
    zIndex: 0,
  },
}));

const CampaignSearchTable = () => {
  const classes = useStyles();

  const { getCampaigns } = useContext(CampaignContext);

  const tableRef = React.createRef();
  const [searchText, setSearchText] = useState("");

  const onEdit = (e, rowData) => {
    window.location.replace(
      `${window.location.origin}/campaign/active/${rowData.domain}/${rowData.campaign_id}`
    );
  };

  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  const columns = [
    {
      field: "laptop",
      title: "Laptop",
      editable: "never",
      cellStyle: {
        width: 50,
        minWidth: 50,
        fontSize: 13,
      },
    },
    {
      field: "domain",
      title: "Domain",
      editable: "never",
      cellStyle: {
        width: 200,
        minWidth: 200,
        fontSize: 13,
      },
    },
    {
      field: "angle",
      title: "Angle",
      editable: "never",
      cellStyle: {
        width: 250,
        minWidth: 250,
        fontSize: 13,
      },
    },
    {
      field: "media_buyer",
      title: "Media Buyer",
      editable: "never",
      cellStyle: {
        width: 100,
        minWidth: 100,
        fontSize: 13,
      },
    },
    {
      field: "created_at",
      title: "Created At",
      editable: "never",
      cellStyle: {
        width: 150,
        minWidth: 150,
        fontSize: 13,
      },
      render: (rowData) => {
        const created_splited = rowData.created_at.split("T");
        return created_splited[0];
      },
    },
  ];

  useEffect(() => {
    // Refresh Table Data
    tableRef.current.onQueryChange();
  }, [searchText]);

  return (
    <Grid>
      {/* Table Search */}
      <Paper elevation={2}>
        <Grid
          item
          container
          justifyContent="space-between"
          alignItems="center"
          className={classes.searchRow}
        >
          <Grid item className={classes.searchTitle}>
            <Typography variant="h6"> Active Campaign </Typography>
          </Grid>
          <Grid item>
            <TextField
              className={classes.searchInput}
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      className={classes.button}
                      type="submit"
                      size="small"
                      disabled={searchText ? false : true}
                      onClick={() => setSearchText("")}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      {/* Table Data */}
      <Grid item>
        <MaterialTable
          style={{zIndex: 0}}
          tableRef={tableRef}
          icons={tableIcons}
          columns={columns}
          data={(query) =>
            new Promise(async (resolve, reject) => {
              let orderBy = "created_at";
              let orderDirection = "desc";

              if (query.orderBy && query.orderDirection) {
                orderBy = query.orderBy.field;
                orderDirection = query.orderDirection;
              }

              const data = await getCampaigns(
                query.page,
                query.pageSize,
                orderBy,
                orderDirection,
                searchText
              );

              if (data) {
                resolve({
                  data: data.campaigns,
                  page: query.page,
                  totalCount: data.total,
                });
              } else {
                reject();
              }
            })
          }
          onRowClick={(event, rowData) => onEdit(event, rowData)}
          options={{
            showTitle: false,
            toolbar: false,
            draggable: false,
            actionsColumnIndex: -1,
            pageSizeOptions: [],
            pageSize: 20,
            padding: "dense",
            headerStyle: {
              fontWeight: "bold",
              fontSize: 13,
              zIndex: 0,
            },
          }}
          components={{
            Pagination: PatchedPagination,
          }}
        />
      </Grid>
    </Grid>
  );
};

export default CampaignSearchTable;
